import { useCallback, useEffect, useRef, useState } from 'react';

import {
  announceChanges, applyStreamFrame, dropTrailingPlaceholder, markInterrupting, settleInterruptedTurn,
  nextActionsProgress, updateActionStatuses, updateNextActionsMetadata, updateQuestionMetadata,
} from '../stream';
import { speechToTextSupported, useSpeechToText } from '../audio/use_speech_to_text';
import { textToSpeechSupported, useTextToSpeech } from '../audio/use_text_to_speech';
import { useAssistant } from './use_assistant';
import { useChatResume } from './use_chat_resume';
import { useTools } from '../tools/context';
import { storeProgress, withStoredProgress } from './use_next_actions';

import type { NextActionQueue } from './use_next_actions';
import type { NextActionsProgress } from '../tools/types';
import type { TurnResult } from './use_turn_subscription';
import type {
  ActionVerdict, Agent, AssistantAction, InputMode, Message, Model, PageContext, Paths, StreamFrame,
} from '../types';

interface Options {
  agents: Agent[];
  pageContext: (messages: Message[]) => PageContext | null;
  paths: Paths;
  visible: boolean;
}

interface SendOptions {
  inputMode?: InputMode;
  speak?: boolean;
}

interface TurnOptions {
  // Patches the transcript for what is about to happen - the message just typed, the status a
  // proposal is moving to - before the placeholder its reply streams into is appended. Left out by
  // a caller that renders its own pending state.
  optimistic?: (messages: Message[]) => Message[];
  // Puts that patch back when nothing came of the turn. The placeholder keeps the error frame
  // applyStreamFrame wrote into it, so the user is still told something went wrong.
  revert?: (messages: Message[]) => Message[];
  speak?: boolean;
  start: (onFrame: (frame: StreamFrame) => void) => Promise<TurnResult>;
}

// Session-scoped: an engaged chat survives navigations and refreshes in this tab, while a new tab
// lands on the greeting with resume offered instead. Left true in a long-lived tab, the flag is
// still inert: it only auto-applies what loadChat returns, and Ai::ResumableChatQuery already
// refuses chats past its idle window, so a stale tab ages out server-side.
const CHAT_ACTIVE_KEY = 'aiAssistantChatActive';

const BRIEF = /^brief\b/i;
const NO = /\b(not|never|no|nope|nah|don'?t|won'?t|can'?t|cannot)\b/i;
const YES = /^(yes|yeah|yep|yup|sure|ok(ay)?|absolutely|definitely|go ahead|do it|let'?s go)\b/i;

const SHORT_REPLY_WORDS = 6;

function accepts(reply: string) {
  const short = reply.split(/\s+/).length <= SHORT_REPLY_WORDS;
  return short && !NO.test(reply) && (BRIEF.test(reply) || YES.test(reply));
}

function chatSessionActive(): boolean {
  try {
    return window.sessionStorage.getItem(CHAT_ACTIVE_KEY) === 'true';
  } catch {
    return false;
  }
}

function markChatSession(active: boolean) {
  try {
    if (active) {
      window.sessionStorage.setItem(CHAT_ACTIVE_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(CHAT_ACTIVE_KEY);
    }
  } catch {
    // The next navigation just falls back to offering resume.
  }
}

export function useConversation({ agents, pageContext, paths, visible }: Options) {
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const {
    agent, ask, loadChat, loading, model, resetChat, resolveAction, resolveQuestions,
    setAgent, setChatId, setModel, stopTurn,
  } = useAssistant(agents, paths, { pageContext: () => pageContext(messagesRef.current) });
  const { setToolCalls } = useTools();
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [resumedChat, setResumedChat] = useState<{ chatId: number; messages: Message[] } | null>(null);

  const startedRef = useRef(false);
  const offerRef = useRef<(() => void) | null>(null);
  const speakTurnRef = useRef(false);
  const announced = useRef(new Set<number>());
  const ttsSupported = textToSpeechSupported();
  const {
    cancel: stopSpeaking, enabled: voiceEnabled, feed, finish, prime, speaking, toggleEnabled: toggleVoice,
  } = useTextToSpeech();

  function addAttachments(added: File[]) {
    setAttachments(attachments => [...attachments, ...added]);
  }

  function removeAttachment(index: number) {
    setAttachments(attachments => attachments.filter((_, position) => position !== index));
  }

  async function runTurn({ optimistic = messages => messages, revert, speak = false, start }: TurnOptions) {
    const interrupted = stopTurn();

    startedRef.current = true;
    const wasActive = chatSessionActive();
    markChatSession(true);
    stopSpeaking();
    speakTurnRef.current = speak;

    setMessages((messages) => {
      const next = optimistic(interrupted ? settleInterruptedTurn(messages) : messages);
      return [
        ...(interrupted ? markInterrupting(next) : next),
        { role: 'assistant', content: '', isStreaming: true },
      ];
    });

    const result = await start((frame) => {
      setMessages(messages => applyStreamFrame(messages, frame));

      if (frame.type === 'done') {
        announceChanges(frame.actions, announced.current);
      }
    });

    if (result.interrupted) {
      return result;
    }

    if (result.noReply) {
      setMessages(dropTrailingPlaceholder);
    }

    if (result.error) {
      if (revert) {
        setMessages(revert);
      }

      if (!wasActive) {
        markChatSession(false);
      }
    }

    setToolCalls(result.toolCalls);
    return result;
  }

  async function sendMessage(prompt: string, options?: SendOptions) {
    const trimmed = prompt.trim();
    if (!trimmed) {
      return;
    }

    const accept = offerRef.current;
    offerRef.current = null;
    if (accept && accepts(trimmed)) {
      accept();
      return;
    }

    const sent = attachments;
    setAttachments([]);

    let spoken = '';
    await runTurn({
      speak: ttsSupported && voiceEnabled && !!options?.speak,
      optimistic: messages => [
        ...messages,
        { role: 'user', content: trimmed, attachments: sent.map(({ name }) => ({ name })) },
      ],
      start: onFrame => ask({
        attachments: sent,
        inputMode: options?.inputMode ?? 'text',
        prompt: trimmed,
        onFrame(frame) {
          onFrame(frame);

          if (!speakTurnRef.current) {
            return;
          }

          if (frame.type === 'delta') {
            spoken += frame.text;
            feed(spoken);
          } else if (frame.type === 'done') {
            finish(frame.content);
          } else if (frame.type === 'error') {
            stopSpeaking();
          }
        },
      }),
    });
  }

  // sendMessage reads this render's attachments, so callers that outlive the render that
  // handed them the function - a window listener, a voice transcript that lands later - go through a
  // stable wrapper rather than a captured copy.
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const send = useCallback((prompt: string, options?: SendOptions) => {
    void sendMessageRef.current(prompt, options);
  }, []);

  const say = useCallback((content: string, accept?: () => void) => {
    setMessages(messages => [...messages, { role: 'assistant', content, local: true }]);
    offerRef.current = accept ?? null;
  }, []);

  async function handleResolveAction(action: AssistantAction, verdict: ActionVerdict) {
    let resolved = false;

    await runTurn({
      optimistic: messages => updateActionStatuses(messages, [
        { ...action, status: verdict === 'apply' ? 'applying' : 'rejected' },
      ]),
      revert: messages => (resolved ? messages : updateActionStatuses(messages, [action])),
      start: onFrame => resolveAction({
        action,
        verdict,
        onResolved: (status, clientChanges) => {
          resolved = true;
          announceChanges([{ ...action, status, clientChanges }], announced.current);
          setMessages(messages => updateActionStatuses(messages, [{ ...action, status }]));
        },
        onFrame,
      }),
    });
  }

  // Answering and closing differ only in what the card is left saying and what the model is told;
  // both spend it and both resume the turn it halted.
  async function handleResolveQuestions(
    answerPath: string,
    spent: { answers?: (string | null)[], dismissed?: boolean }
  ) {
    let resolved = false;

    await runTurn({
      optimistic: messages => updateQuestionMetadata(messages, answerPath, spent),
      revert: messages => (
        resolved ? messages : updateQuestionMetadata(messages, answerPath, { answers: undefined, dismissed: undefined })
      ),
      start: onFrame => resolveQuestions({
        ...spent,
        answerPath,
        onResolved: () => {
          resolved = true;
        },
        onFrame,
      }),
    });
  }

  function updateNextActions(
    queue: NextActionQueue,
    change: (progress: NextActionsProgress) => NextActionsProgress
  ) {
    const progress = change(nextActionsProgress(messagesRef.current, queue.id));
    storeProgress(queue.id, progress);
    messagesRef.current = updateNextActionsMetadata(messagesRef.current, queue.id, progress);
    setMessages(messages => updateNextActionsMetadata(messages, queue.id, progress));
  }

  const sttSupported = speechToTextSupported();
  const { cancel: cancelRecording, recording, toggle: toggleRecording } = useSpeechToText({
    onTranscript: setInput,
    onFinish: (transcript: string) => {
      setInput('');
      send(transcript, { inputMode: 'voice', speak: true });
    },
  });

  // Fetch the most recent chat the first time the conversation is shown, unless a send already
  // began (see startedRef in sendMessage). On a fresh session it's held for an explicit "Resume
  // chatting" so first open lands on the greeting; mid-session (navigation, refresh) it's applied
  // directly so the conversation doesn't appear to vanish.
  const resuming = useChatResume({
    open: visible,
    loadChat,
    hasStarted: () => startedRef.current,
    // Nothing resumable means the marker outlived its chat (they expire server-side after an idle
    // window); clearing it spares later opens in this tab the spinner for an empty lookup.
    onEmpty: () => markChatSession(false),
    onResume: (resumed) => {
      offerRef.current = null;
      if (chatSessionActive()) {
        setMessages(withStoredProgress(resumed.messages));
        setChatId(resumed.chatId);
      } else {
        setResumedChat(resumed);
      }
    },
  });

  function resumePreviousChat() {
    if (!resumedChat) return;

    offerRef.current = null;
    markChatSession(true);
    setMessages(withStoredProgress(resumedChat.messages));
    setChatId(resumedChat.chatId);
    setResumedChat(null);
  }

  useEffect(() => {
    if (!visible) {
      stopSpeaking();
    }
  }, [visible, stopSpeaking]);

  function startNewChat() {
    startedRef.current = true;
    offerRef.current = null;
    markChatSession(false);
    stopSpeaking();
    cancelRecording();
    setMessages([]);
    setInput('');
    setAttachments([]);
    setToolCalls();
    void resetChat();
  }

  function changeAgent(agent: Agent) {
    setAgent(agent);
  }

  function changeModel(model: Model) {
    setModel(model);
  }

  function submit() {
    send(input);
    setInput('');
  }

  function toggleMic() {
    // Prime speech synthesis inside this gesture so iOS lets us speak the reply later.
    if (!recording) {
      prime();
    }

    toggleRecording();
  }

  return {
    agent,
    attachments,
    busy: loading || resuming,
    hasResumableChat: resumedChat != null,
    input,
    messages,
    model,
    recording,
    // The fetch that is about to repopulate the transcript unprompted; the greeting would only
    // flash and get replaced, so the transcript holds a spinner instead.
    restoring: resuming && chatSessionActive(),
    resuming,
    send,
    speaking,
    sttSupported,
    ttsSupported,
    voiceEnabled,
    addAttachments,
    answerQuestions: (answerPath: string, answers: (string | null)[]) => (
      handleResolveQuestions(answerPath, { answers })
    ),
    changeAgent,
    changeInput: setInput,
    changeModel,
    dismissQuestions: (answerPath: string) => handleResolveQuestions(answerPath, { dismissed: true }),
    removeAttachment,
    resolveAction: handleResolveAction,
    resumePreviousChat,
    say,
    startNewChat,
    stopSpeaking,
    submit,
    toggleMic,
    toggleVoice,
    updateNextActions,
  };
}
